import type { ReactNode } from 'react';
import { DatePicker, Input, InputNumber, Select } from 'antd';
import type { FormInstance } from 'antd';
import moment from 'moment';
import type { Moment } from 'moment';

import type { PromoFieldConfig } from '../../../config/tpoGenConfig';
import type { ResolvedDateConstraints } from '../../../rules/types';
import { toMoment } from '../../../utils/dateCoerce';
import { tpoOptionTestId, tpoPartTestId, tpoStateAttrs } from '../../../config/testIds';
import UnitToggle from './UnitToggle';
import RangeInput from './RangeInput';

type Option = { label: string; value: string };
type UnitOption = { label: string; value: string; disabled?: boolean };

/** Adds a per-option test id derived from the option `value`, never the label. */
const withOptionTestIds = (options: Option[], baseTestId: string | undefined): Option[] =>
    baseTestId ? options.map((o) => ({ ...o, 'data-testid': tpoOptionTestId(baseTestId, o.value) })) : options;

/**
 * The promo form has no per-field wrapper element — `Form.Item` clones whatever
 * this returns — so the test id and the rule-state attributes both go on the
 * control itself. AntD forwards `data-*` through to the DOM for every control
 * used here (pinned in `config/__tests__/antdTestIdForwarding.test.tsx`).
 */
const renderPromoField = (
    field: PromoFieldConfig,
    options: Option[],
    disabled: boolean,
    formValues: Record<string, any>,
    form: FormInstance,
    planYear?: number,
    onValuesChange?: (changedValues: Record<string, unknown>) => void,
    dateConstraints?: ResolvedDateConstraints,
    unitOptions?: UnitOption[],
    testId?: string,
    required?: boolean,
): ReactNode => {
    const stateAttrs = tpoStateAttrs({ disabled, required });
    // Engine-derived (disabled-aware) unit options override the static config list
    // so the discount-unit toggle is governed by the same rule engine as the field.
    const resolvedUnitToggle = field.unitToggle
        ? { ...field.unitToggle, options: unitOptions ?? field.unitToggle.options }
        : undefined;
    switch (field.type) {
        case 'datepicker': {
            const minDate = toMoment(dateConstraints?.minDate);
            const maxDate = toMoment(dateConstraints?.maxDate);
            const disablePast = dateConstraints?.disablePast;
            const planYearMinDate =
                dateConstraints?.minFromContext === 'planYearStart' && typeof planYear === 'number'
                    ? moment().year(planYear).startOf('year')
                    : undefined;
            const hasConstraints = !!(minDate || maxDate || disablePast || planYearMinDate);
            const disabledDate = hasConstraints
                ? (current: Moment) => {
                      if (!current) return false;
                      if (planYearMinDate && current.isBefore(planYearMinDate, 'day')) return true;
                      if (disablePast && current.isBefore(moment(), 'day')) return true;
                      if (minDate && current.isBefore(minDate, 'day')) return true;
                      if (maxDate && current.isAfter(maxDate, 'day')) return true;
                      return false;
                  }
                : undefined;
            const defaultPickerCandidates = [
                planYearMinDate,
                disablePast ? moment().startOf('day') : undefined,
                minDate,
            ].filter((value): value is Moment => !!value);
            const defaultPickerValue =
                defaultPickerCandidates.length > 0
                    ? defaultPickerCandidates.reduce((latestDate, candidateDate) =>
                          candidateDate.isAfter(latestDate, 'day') ? candidateDate : latestDate,
                      )
                    : undefined;
            return (
                <DatePicker
                    placeholder={field.placeholder}
                    allowClear={false}
                    disabled={disabled}
                    disabledDate={disabledDate}
                    defaultPickerValue={defaultPickerValue}
                    style={{ width: '100%', borderRadius: 6 }}
                    data-testid={testId}
                    {...stateAttrs}
                />
            );
        }
        case 'select':
            return (
                <Select
                    placeholder={field.placeholder}
                    options={withOptionTestIds(options, testId)}
                    disabled={disabled}
                    allowClear={field.allowClear ?? true}
                    data-testid={testId}
                    {...stateAttrs}
                />
            );
        case 'offerlibrary':
            // TODO: integrate lazy-fetch of promo library mechanics on dropdown open
            return (
                <Select
                    placeholder={field.placeholder}
                    options={withOptionTestIds(options, testId)}
                    disabled={disabled}
                    allowClear
                    data-testid={testId}
                    {...stateAttrs}
                />
            );
        case 'number': {
            const unitAddon = resolvedUnitToggle ? (
                <UnitToggle
                    config={resolvedUnitToggle}
                    value={formValues[resolvedUnitToggle.unitField]}
                    onChange={(unitValue) => {
                        form.setFieldsValue({ [resolvedUnitToggle.unitField]: unitValue });
                        onValuesChange?.({ [resolvedUnitToggle.unitField]: unitValue });
                    }}
                    disabled={disabled}
                    testId={testId ? tpoPartTestId(testId, 'unit') : undefined}
                />
            ) : null;
            return (
                <InputNumber
                    placeholder={field.placeholder}
                    disabled={disabled}
                    style={{ width: '100%' }}
                    addonAfter={unitAddon}
                    data-testid={testId}
                    {...stateAttrs}
                />
            );
        }
        case 'range': {
            const unitAddon = resolvedUnitToggle ? (
                <UnitToggle
                    config={resolvedUnitToggle}
                    value={formValues[resolvedUnitToggle.unitField]}
                    onChange={(unitValue) => {
                        form.setFieldsValue({ [resolvedUnitToggle.unitField]: unitValue });
                        onValuesChange?.({ [resolvedUnitToggle.unitField]: unitValue });
                    }}
                    disabled={disabled}
                    testId={testId ? tpoPartTestId(testId, 'unit') : undefined}
                />
            ) : null;
            return (
                <RangeInput
                    labels={field.rangeLabels ?? { first: 'Min', second: 'Max' }}
                    placeholder={field.placeholder}
                    disabled={disabled}
                    unitToggle={unitAddon}
                    testId={testId}
                />
            );
        }
        default:
            return (
                <Input
                    placeholder={field.placeholder}
                    disabled={disabled}
                    maxLength={field.maxCharLimit}
                    data-testid={testId}
                    {...stateAttrs}
                />
            );
    }
};

export default renderPromoField;
